import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Landmark, Plus, ChevronRight, CreditCard, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { downloadReceipt } from "@/lib/receipt";

const METHODS = [
  { id: "bank", label: "Bank details", Icon: Landmark, blurb: "Send to any external bank account." },
  { id: "card", label: "Bank card", Icon: CreditCard, blurb: "Payout to a card you've added." },
  { id: "paypal", label: "PayPal", Icon: Wallet, blurb: "Send to a PayPal email address." },
];

export default function Withdraw() {
  const { user, balance, refreshBalance } = useAuth();
  const isLiveUser = user?.banking_mode === "live";
  const [mode, setMode] = useState(isLiveUser ? "live" : (user?.banking_mode || "demo"));
  const [method, setMethod] = useState("bank");
  const [amount, setAmount] = useState("");

  // bank fields
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountHolder, setAccountHolder] = useState("");

  // card / paypal fields
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState("");
  const [paypalEmail, setPaypalEmail] = useState("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/wallet/banks").then(({ data }) => {
      const verified = (data.banks || []).filter((b) => b.verified);
      setBanks(verified);
      if (verified.length > 0) setSelectedBank(verified[0].id);
    });
    api.get("/wallet/cards").then(({ data }) => {
      const list = data.cards || [];
      setCards(list);
      if (list.length > 0) setSelectedCard(list[0].id);
    });
  }, []);

  useEffect(() => { if (isLiveUser && mode !== "live") setMode("live"); }, [isLiveUser, mode]);

  const available = balance ? (mode === "demo" ? balance.balance_demo : balance.balance_live) : 0;
  const value = Math.max(0, Number(amount) || 0);

  const submit = async () => {
    if (value <= 0) { toast.error("Enter an amount greater than zero."); return; }
    if (value > available) { toast.error("Amount exceeds available balance."); return; }
    let body = { amount: value, mode, method };
    if (method === "bank") {
      if (selectedBank) body.external_bank_id = selectedBank;
      else {
        if (!bankName || !accountNumber || !accountHolder) { toast.error("Fill in all bank details."); return; }
        body = { ...body, bank_name: bankName.trim(), account_number: accountNumber.trim(), account_holder: accountHolder.trim() };
      }
    } else if (method === "card") {
      if (!selectedCard) { toast.error("Pick a saved card or add one first."); return; }
      body.card_id = selectedCard;
    } else if (method === "paypal") {
      if (!paypalEmail.includes("@")) { toast.error("Enter a valid PayPal email."); return; }
      body.paypal_email = paypalEmail.trim().toLowerCase();
    }
    setLoading(true);
    try {
      const { data } = await api.post("/wallet/withdraw", body);
      await refreshBalance();
      if (data.status === "pending_approval") {
        toast.success(`Withdrawal of $${value.toFixed(2)} submitted. Pending admin approval.`);
      } else {
        toast.success(`Withdrew $${value.toFixed(2)}.`);
      }
      if (data?.transaction_id) downloadReceipt(data.transaction_id, { silent: true });
      setAmount(""); setBankName(""); setAccountNumber(""); setAccountHolder("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-3xl">
        <div className="text-eyebrow text-[#4F4D45]">Move money out</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Withdraw</h1>
        <p className="text-[#4F4D45] mt-2">Choose how the funds reach you.{mode === "live" && " Live withdrawals require admin approval."}</p>

        <div className="mt-8 fv-card p-6 md:p-8">
          {isLiveUser ? (
            <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#1B3526]/10 text-[#1B3526] font-medium" data-testid="withdraw-live-only">
              Live account · funds settle on admin approval
            </div>
          ) : (
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="bg-[#E3DECF]">
                <TabsTrigger value="demo" data-testid="withdraw-tab-demo">Demo</TabsTrigger>
                <TabsTrigger value="live" data-testid="withdraw-tab-live">Live</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <div className="mt-6 flex items-center justify-between p-4 rounded-xl bg-[#F0ECE0] border border-[#D2CDBE]">
            <div className="text-sm text-[#4F4D45]">Available {mode}</div>
            <div className="font-display text-xl font-semibold" data-testid="withdraw-available">${available.toFixed(2)}</div>
          </div>

          {/* Method picker */}
          <div className="mt-6">
            <Label className="text-eyebrow text-[#4F4D45]">Withdrawal method</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {METHODS.map(({ id, label, Icon, blurb }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMethod(id)}
                  data-testid={`withdraw-method-${id}`}
                  className={`flex flex-col items-start gap-2 px-3 py-3 rounded-xl border text-left transition-colors ${method === id ? "border-[#1B3526] bg-[#1B3526]/[0.06]" : "border-white/10 bg-white/[0.04] hover:border-white/20"}`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.6} />
                  <div>
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-[11px] text-[#4F4D45] mt-0.5 leading-snug">{blurb}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Method-specific fields */}
          {method === "bank" && (
            <div className="mt-6">
              {banks.length > 0 ? (
                <>
                  <Label className="text-eyebrow text-[#4F4D45]">Pay to</Label>
                  <div className="mt-2 space-y-2">
                    {banks.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setSelectedBank(b.id)}
                        data-testid={`withdraw-bank-${b.id}`}
                        className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${selectedBank === b.id ? "border-[#1B3526] bg-[#1B3526]/[0.04]" : "border-white/10 bg-white/[0.04] hover:border-white/20"}`}
                      >
                        <Landmark className="w-5 h-5" strokeWidth={1.6} />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{b.nickname || b.bank_name}</div>
                          <div className="text-xs text-[#4F4D45]">{b.account_holder} · ****{b.last4}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <Link to="/app/banks" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#1B3526] hover:underline" data-testid="withdraw-manage-banks">
                    <Plus className="w-3.5 h-3.5" /> Add or manage banks <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2 text-sm text-[#4F4D45]">
                    You don't have a verified external bank yet. <Link to="/app/banks" className="text-[#1B3526] underline">Add one</Link> for faster future withdrawals, or enter the details below for a one-time payout.
                  </div>
                  <div>
                    <Label className="text-eyebrow text-[#4F4D45]">Bank name</Label>
                    <Input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. Chase Bank" data-testid="withdraw-bank-name" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-eyebrow text-[#4F4D45]">Account holder</Label>
                    <Input value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} placeholder="Full name on account" data-testid="withdraw-account-holder" className="mt-1" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-eyebrow text-[#4F4D45]">Account number</Label>
                    <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\s/g, ""))} placeholder="Bank account number" data-testid="withdraw-account-number" className="mt-1" />
                  </div>
                </div>
              )}
            </div>
          )}

          {method === "card" && (
            <div className="mt-6">
              {cards.length > 0 ? (
                <>
                  <Label className="text-eyebrow text-[#4F4D45]">Pay to card</Label>
                  <div className="mt-2 space-y-2">
                    {cards.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedCard(c.id)}
                        data-testid={`withdraw-card-${c.id}`}
                        className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${selectedCard === c.id ? "border-[#1B3526] bg-[#1B3526]/[0.04]" : "border-white/10 bg-white/[0.04] hover:border-white/20"}`}
                      >
                        <CreditCard className="w-5 h-5" strokeWidth={1.6} />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{c.brand} •••• {c.last4}</div>
                          <div className="text-xs text-[#4F4D45]">{c.card_holder} · expires {c.expiry}{c.verification_method === "paypal_auth_void" ? " · verified" : ""}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <Link to="/app/cards" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#1B3526] hover:underline">
                    <Plus className="w-3.5 h-3.5" /> Add or manage cards <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </>
              ) : (
                <div className="text-sm text-[#4F4D45]">
                  You don't have any saved cards. <Link to="/app/cards" className="text-[#1B3526] underline">Add one</Link> to receive card payouts.
                </div>
              )}
            </div>
          )}

          {method === "paypal" && (
            <div className="mt-6">
              <Label className="text-eyebrow text-[#4F4D45]">PayPal email</Label>
              <Input
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                placeholder="you@paypal.com"
                type="email"
                data-testid="withdraw-paypal-email"
                className="mt-1"
              />
              <div className="mt-2 text-xs text-[#4F4D45]">
                Funds go to your PayPal balance. Make sure the email matches your PayPal account.
              </div>
            </div>
          )}

          <div className="mt-6">
            <Label className="text-eyebrow text-[#4F4D45]">Amount (USD)</Label>
            <div className="relative mt-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4F4D45]">$</span>
              <Input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} className="pl-8 text-2xl font-display h-14" data-testid="withdraw-amount-input" />
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button onClick={submit} disabled={loading} className="bg-[#1B3526] hover:bg-[#0F2517]" data-testid="withdraw-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "live" ? `Request $${value.toFixed(2)} withdrawal` : `Withdraw $${value.toFixed(2)}`)}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
